/**
 * State management utilities for orchestration selections.
 *
 * This module provides hooks for managing complex selection state for
 * different types of orchestrations (model inference, post-processing,
 * quantification, etc.). The hooks encapsulate the logic for updating
 * nested selection structures in a predictable way.
 */

import { InferenceModelData, OrchestrationInference } from 'interfaces/calculateFeatures';
import { cloneDeep, filter, forEach, get, isEmpty, keys, omit } from 'es-toolkit/compat';
import React, { useCallback, useMemo, useState } from 'react';

/**
 * Structure for tracking selected orchestrations by slide and type
 */
export interface OrchestrationBySlideByType {
  [slideId: string]: {
    [modelType: string]: {
      orchestration: OrchestrationInference;
      model: InferenceModelData;
    };
  };
}

/**
 * Structure for tracking selected orchestrations by slide and result name
 */
export interface OrchestrationBySlideByResultName {
  [slideId: string]: {
    [resultName: string]: {
      orchestration: OrchestrationInference;
    };
  };
}

/**
 * Data structure for organizing orchestrations by stain type and result name
 */
export interface OrchestrationByStainByResultName {
  [stain: string]: {
    [resultName: string]: OrchestrationInference[];
  };
}

/**
 * Creates a generic updater function for result-based orchestration selections.
 * This function factory reduces code duplication for different result types.
 */
const createOrchestrationUpdater =
  (setterFunction: React.Dispatch<React.SetStateAction<OrchestrationBySlideByResultName>>) =>
  (slideIds: string[], resultKey?: string, orchestration?: OrchestrationInference) => {
    setterFunction((prev) => {
      const newOrchestrations = { ...prev };

      if (isEmpty(resultKey)) {
        // Clear all selections for these slides
        forEach(slideIds, (slideId) => {
          newOrchestrations[slideId] = {};
        });
      } else if (isEmpty(orchestration)) {
        // Remove specific result selection
        forEach(slideIds, (slideId) => {
          newOrchestrations[slideId] = omit(newOrchestrations[slideId], resultKey);
        });
      } else {
        // Add/update selection
        forEach(slideIds, (slideId) => {
          newOrchestrations[slideId] = {
            ...newOrchestrations[slideId],
            [resultKey]: {
              orchestration,
            },
          };
        });
      }

      return newOrchestrations;
    });
  };

/**
 * Hook for managing orchestration selections across different types.
 *
 * This hook provides state and methods for managing selections of
 * model orchestrations, post-processing results, and other artifacts.
 * It handles the complex nested state updates required for multi-slide,
 * multi-type selections.
 *
 * @returns Object containing all selection states and update methods
 */
export const useOrchestrationSelections = () => {
  const [selectedOrchestrations, setSelectedOrchestrations] = useState<OrchestrationBySlideByType>({});
  const [selectedPostprocessedOrchestrations, setSelectedPostprocessedOrchestrations] =
    useState<OrchestrationBySlideByResultName>({});
  const [selectedPipsOrchestrations, setSelectedPipsOrchestrations] = useState<OrchestrationBySlideByResultName>({});
  const [selectedPipsAreaInstancesOrchestrations, setSelectedPipsAreaInstancesOrchestrations] =
    useState<OrchestrationBySlideByResultName>({});
  const [selectedCellQuantificationOrchestrations, setSelectedCellQuantificationOrchestrations] =
    useState<OrchestrationBySlideByResultName>({});
  const [selectedAreaQuantificationOrchestrations, setSelectedAreaQuantificationOrchestrations] =
    useState<OrchestrationBySlideByResultName>({});
  const [selectedSlideSegmentationsTableOrchestrations, setSelectedSlideSegmentationsTableOrchestrations] =
    useState<OrchestrationBySlideByResultName>({});
  const [selectedSlideDetectionsTableOrchestrations, setSelectedSlideDetectionsTableOrchestrations] =
    useState<OrchestrationBySlideByResultName>({});

  /**
   * Updates orchestration selections when model type changes.
   * Moves existing selections from old model type to new model type.
   */
  const setSelectedOrchestrationsByClassificationType = useCallback((modelType: string, previousModelType?: string) => {
    if (!previousModelType) return;

    setSelectedOrchestrations((currentSelectedOrchestrations) => {
      const slideIdsWithPreviousModelType = filter(
        keys(selectedOrchestrations),
        (slideId) => !isEmpty(get(selectedOrchestrations[slideId], previousModelType))
      );

      if (isEmpty(slideIdsWithPreviousModelType)) {
        return currentSelectedOrchestrations;
      }

      const newOrchestrations = { ...currentSelectedOrchestrations };

      forEach(slideIdsWithPreviousModelType, (slideId) => {
        if (!isEmpty(modelType)) {
          newOrchestrations[slideId] = {
            ...newOrchestrations[slideId],
            [modelType]: get(newOrchestrations[slideId], previousModelType),
          };
        }

        newOrchestrations[slideId] = omit(newOrchestrations[slideId], previousModelType);
      });

      return newOrchestrations;
    });
  }, []);

  /**
   * Updates model orchestration selections for specified slides.
   * Can add, remove, or clear selections based on provided parameters.
   */
  const setSelectedOrchestrationsByTypeByStain = useCallback(
    (slideIds: string[], model?: InferenceModelData, modelType?: string, orchestration?: OrchestrationInference) => {
      setSelectedOrchestrations((currentSelectedOrchestrations) => {
        const newOrchestrations = cloneDeep(currentSelectedOrchestrations);

        if (isEmpty(model)) {
          // Clear all selections for these slides
          forEach(slideIds, (slideId) => {
            newOrchestrations[slideId] = {};
          });
        } else if (isEmpty(orchestration)) {
          // Remove specific model type selection
          forEach(slideIds, (slideId) => {
            newOrchestrations[slideId] = omit(newOrchestrations[slideId], modelType);
          });
        } else {
          // Add/update selection
          forEach(slideIds, (slideId) => {
            newOrchestrations[slideId] = {
              ...newOrchestrations[slideId],
              [modelType]: {
                orchestration,
                model,
              },
            };
          });
        }

        return newOrchestrations;
      });
    },
    []
  );

  /**
   * Updates post-processing orchestration selections for specified slides.
   */
  const setSelectedPostprocessedOrchestrationsByFlowClassNameByStain = useCallback(
    (slideIds: string[], flowClassName?: string, orchestration?: OrchestrationInference) => {
      setSelectedPostprocessedOrchestrations((prev) => {
        const newOrchestrations = { ...prev };

        if (isEmpty(flowClassName)) {
          // Clear all selections for these slides
          forEach(slideIds, (slideId) => {
            newOrchestrations[slideId] = {};
          });
        } else if (isEmpty(orchestration)) {
          // Remove specific flow class selection
          forEach(slideIds, (slideId) => {
            newOrchestrations[slideId] = omit(newOrchestrations[slideId], flowClassName);
          });
        } else {
          // Add/update selection
          forEach(slideIds, (slideId) => {
            newOrchestrations[slideId] = {
              ...newOrchestrations[slideId],
              [flowClassName]: {
                orchestration,
              },
            };
          });
        }

        return newOrchestrations;
      });
    },
    []
  );

  const allSelectedNonModelOrchestrations: OrchestrationBySlideByResultName[] = useMemo(() => {
    return [
      selectedPostprocessedOrchestrations,
      selectedPipsOrchestrations,
      selectedPipsAreaInstancesOrchestrations,
      selectedCellQuantificationOrchestrations,
      selectedAreaQuantificationOrchestrations,
      selectedSlideSegmentationsTableOrchestrations,
      selectedSlideDetectionsTableOrchestrations,
    ];
  }, [
    selectedPostprocessedOrchestrations,
    selectedPipsOrchestrations,
    selectedPipsAreaInstancesOrchestrations,
    selectedCellQuantificationOrchestrations,
    selectedAreaQuantificationOrchestrations,
    selectedSlideSegmentationsTableOrchestrations,
    selectedSlideDetectionsTableOrchestrations,
  ]);

  return {
    // State
    selectedOrchestrations,
    selectedPostprocessedOrchestrations,
    selectedPipsOrchestrations,
    selectedPipsAreaInstancesOrchestrations,
    selectedCellQuantificationOrchestrations,
    selectedAreaQuantificationOrchestrations,
    selectedSlideSegmentationsTableOrchestrations,
    selectedSlideDetectionsTableOrchestrations,
    allSelectedNonModelOrchestrations,

    // Setters
    setSelectedOrchestrations,
    setSelectedPostprocessedOrchestrations,
    setSelectedPipsOrchestrations,
    setSelectedPipsAreaInstancesOrchestrations,
    setSelectedCellQuantificationOrchestrations,
    setSelectedAreaQuantificationOrchestrations,
    setSelectedSlideSegmentationsTableOrchestrations,
    setSelectedSlideDetectionsTableOrchestrations,

    // Update methods
    setSelectedOrchestrationsByClassificationType,
    setSelectedOrchestrationsByTypeByStain,
    setSelectedPostprocessedOrchestrationsByFlowClassNameByStain,

    // Generic updaters for different result types
    updateSelectedOrchestrations: createOrchestrationUpdater(setSelectedOrchestrations),
    updateSelectedPostprocessedOrchestrations: createOrchestrationUpdater(setSelectedPostprocessedOrchestrations),
    updateSelectedPipsOrchestrations: createOrchestrationUpdater(setSelectedPipsOrchestrations),
    updateSelectedPipsAreaInstancesOrchestrations: createOrchestrationUpdater(
      setSelectedPipsAreaInstancesOrchestrations
    ),
    updateSelectedCellQuantificationOrchestrations: createOrchestrationUpdater(
      setSelectedCellQuantificationOrchestrations
    ),
    updateSelectedAreaQuantificationOrchestrations: createOrchestrationUpdater(
      setSelectedAreaQuantificationOrchestrations
    ),
    updateSelectedSlideSegmentationsTableOrchestrations: createOrchestrationUpdater(
      setSelectedSlideSegmentationsTableOrchestrations
    ),
    updateSelectedSlideDetectionsTableOrchestrations: createOrchestrationUpdater(
      setSelectedSlideDetectionsTableOrchestrations
    ),
  };
};
